// A running shift: the world of a level, the real-time loop with time acceleration, the instructor (hints in
// training levels) and the bridge to UI, audio and speech.
import type { Airport } from '../sim/airport';
import { World } from '../sim/world';
import { generateTraffic } from '../sim/traffic';
import type { LevelDef } from '../career/levels';
import type { Cmd, Transmission } from '../sim/commands';
import type { SimEvent } from '../sim/scoring';
import { parseInstruction, type ParseCtx, type ParseResult } from '../phraseology/parser';
import { atcTransmission } from '../phraseology/messages';
import { requestRole } from '../sim/world';

export interface Hint { id: number; t: number; text: string; cs?: string }

const STEP = 0.5;
let hintId = 1;

export class GameSession {
  readonly world: World;
  timeScale = 1;
  paused = false;
  ended = false;
  selected: string | null = null;
  hints: Hint[] = [];
  /** Increments whenever something visible changed (for React panels). */
  version = 0;
  private acc = 0;
  private timer = 0;
  private last = 0;
  private listeners = new Set<() => void>();
  private uiAcc = 0;
  private hinted = new Set<string>();
  readonly unsub: (() => void)[] = [];
  onEnd: (() => void) | null = null;

  constructor(readonly level: LevelDef, readonly airport: Airport, seed: number) {
    const spawns = generateTraffic(airport.meta, airport, level.traffic, seed);
    const start = new Date();
    // local (CET/CEST) start hour → UTC, today
    const offset = isSummer(start) ? 2 : 1;
    const utc = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), level.startHour - offset, Math.floor(seed % 4) * 7, 0));
    this.world = new World({
      meta: airport.meta, airport, seed, startUtc: utc, weather: level.weather, configId: level.configId,
      playerRoles: level.roles, readbackErrorRate: level.readbackErrorRate, assists: level.assists, spawns, duration: level.minutes * 60,
    });
    this.unsub.push(this.world.onEvent(e => this.onEvent(e)));
    this.unsub.push(this.world.onTransmission(tx => this.onTx(tx)));
    if (level.assists.hints) this.hint(this.introHint());
  }

  // ------------------------------------------------------------------ loop
  /** The simulation runs on a timer (independent of the display frame rate); drawing uses animation frames. */
  start(): void {
    if (this.timer) return;
    this.last = performance.now();
    this.timer = window.setInterval(() => {
      const now = performance.now();
      const dt = Math.min(1, (now - this.last) / 1000);
      this.last = now;
      if (!this.paused && !this.ended) this.advance(dt * this.timeScale);
      this.uiAcc += dt;
      if (this.uiAcc > 0.25) { this.uiAcc = 0; this.version++; for (const l of this.listeners) l(); }
    }, 40);
  }
  stop(): void { window.clearInterval(this.timer); this.timer = 0; }
  dispose(): void { this.stop(); for (const u of this.unsub) u(); this.unsub.length = 0; }
  advance(simSeconds: number): void {
    this.acc += simSeconds;
    while (this.acc >= STEP) {
      this.acc -= STEP;
      this.world.tick(STEP);
      this.instructor();
      if (this.world.t >= this.level.minutes * 60 || this.world.score.relieved) { this.finish(); break; }
    }
  }
  /** Fraction of the current simulation step for smooth drawing. */
  get alpha(): number { return this.acc / STEP; }
  subscribe(fn: () => void): () => void { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  notify(): void { this.version++; for (const l of this.listeners) l(); }
  finish(): void {
    if (this.ended) return;
    this.ended = true;
    this.notify();
    this.onEnd?.();
  }
  get remaining(): number { return Math.max(0, this.level.minutes * 60 - this.world.t); }

  // ------------------------------------------------------------------ input
  parseCtx(): ParseCtx {
    const w = this.world, ap = this.airport;
    return {
      aircraft: w.aircraft.map(a => ({ cs: a.cs, telephony: a.fp.telephony, flightId: a.fp.flightId })),
      fixes: Object.keys(w.meta.fixes),
      fixSay: Object.fromEntries(Object.entries(w.meta.fixes).map(([k, f]) => [(f.say ?? k).toLowerCase(), k])),
      taxiways: ap.taxiwayNames(),
      runways: Object.keys(ap.runwayEnds),
      stands: Object.keys(ap.stands),
      units: Object.fromEntries(w.meta.units.flatMap(u => u.roles.map(r => [r, u.callsign]))),
      defaults: { depRwy: w.config.dep[0]!, arrRwy: w.config.arr[0]! },
      selected: this.selected,
      clearanceFor: cs => { const p = w.pilot(cs); return p ? w.defaultClearance(p) : null; },
      holdFor: cs => { const p = w.pilot(cs); const h = p ? w.chooseDepHold(p) : null; return p && h ? { rwy: p.depRwy ?? w.config.dep[0]!, hold: h.name } : null; },
      standFor: cs => { const p = w.pilot(cs); return p && !p.isDeparture ? p.ac.fp.stand ?? null : null; },
    };
  }
  parse(text: string): ParseResult { return parseInstruction(text, this.parseCtx()); }

  /** Sends instructions as the player. Returns a German warning, if any. */
  issue(cs: string, cmds: Cmd[]): string | null {
    if (!cmds.length) return 'Keine Anweisung erkannt.';
    const warn = this.world.playerIssue(cs, cmds);
    this.notify();
    return warn;
  }
  /** Preview of what the controller says. */
  preview(cs: string, cmds: Cmd[]): string {
    const ac = this.world.ac(cs);
    if (!ac || !cmds.length) return '';
    return atcTransmission(ac.fp, cmds, this.world.phraseCtx()).text;
  }
  setTimeScale(s: number): void { if (this.level.assists.timeControl || s === 1) this.timeScale = s; this.notify(); }
  togglePause(): void { if (this.level.assists.timeControl) this.paused = !this.paused; this.notify(); }

  // ------------------------------------------------------------------ instructor
  private hint(text: string, cs?: string): void {
    this.hints.push({ id: hintId++, t: this.world.t, text, cs });
    if (this.hints.length > 30) this.hints.shift();
    this.notify();
  }
  private introHint(): string {
    const r = this.level.roles;
    if (r.length === 1 && r[0] === 'DEL') return 'Ich bin deine Ausbilderin. Sobald sich ein Pilot meldet, erscheint sein Streifen oben in der Liste „Anrufe“. Wähle ihn aus und erteile die Freigabe – über die Schaltfläche oder per Eingabe, z. B. „DLH4TK C“ für die vorgeschlagene Freigabe.';
    if (r.includes('GND')) return 'Pushback-Anfragen kommen von den Positionen am Vorfeld. Prüfe, ob die Rollgasse hinter dem Flugzeug frei ist, bevor du „push and start-up approved“ gibst.';
    if (r.includes('TWR')) return 'Behalte die Piste und den Endanflug im Blick. Das Anflugband rechts zeigt dir, wie weit die nächste Landung entfernt ist.';
    return 'Anrufe erscheinen in der Liste rechts. Klicke ein Ziel an, um ihm Anweisungen zu geben – Steuerkurs, Höhe, Geschwindigkeit, Direktflug, ILS-Freigabe oder Frequenzwechsel.';
  }
  private onEvent(e: SimEvent): void {
    if (!this.level.assists.hints) return;
    const map: Record<string, string> = {
      RB_MISSED: 'Das Readback war falsch – du hättest den Wert wiederholen müssen, statt „readback correct“ zu sagen.',
      UNANSWERED: 'Ein Pilot musste seinen Anruf wiederholen. Antworte zügig, auch wenn es nur „stand by“ wäre.',
      NOT_ON_FREQ: 'Der Pilot hört dich nicht – er ist noch auf einer anderen Frequenz.',
      GA_NO_CLR: 'Ohne Landefreigabe muss der Pilot durchstarten. Erteile die Landefreigabe spätestens bei 2 NM.',
      SEPARATION: 'Staffelungsunterschreitung! Gib frühzeitig Höhen oder Kurse, die die Flugzeuge trennen.',
    };
    const t = map[e.code];
    if (t && !this.hinted.has(e.code)) { this.hinted.add(e.code); this.hint(t, e.cs); }
  }
  private onTx(tx: Transmission): void {
    if (!this.level.assists.hints || !tx.rbError) return;
    if (!this.world.playerUnits().includes(tx.freq)) return;
    if (!this.hinted.has('RB_ERR_EAR')) { this.hinted.add('RB_ERR_EAR'); this.hint(`Hör genau hin: Hat ${tx.cs} alles richtig zurückgelesen? Vergleiche mit deiner Anweisung.`, tx.cs); }
  }
  private instructor(): void {
    if (!this.level.assists.hints) return;
    const w = this.world;
    for (const r of w.requests) {
      if (!w.playerUnits().includes(r.unit)) continue;
      const role = requestRole(r.kind);
      if (role && !w.isPlayerRole(role)) continue;
      const key = `${r.kind}`;
      if (this.hinted.has(key)) continue;
      const txt: Partial<Record<typeof r.kind, string>> = {
        CLEARANCE: `${r.cs} möchte seine Streckenfreigabe. Die vorgeschlagene SID, Höhe und den Squawk siehst du im Streifen – „Freigabe“ klicken oder „${r.cs} C“ tippen.`,
        PUSH: `${r.cs} ist bereit zum Pushback. Erst prüfen, ob dahinter niemand rollt – dann „Push & Start“.`,
        STARTUP: `${r.cs} steht auf einer Position ohne Pushback und möchte anlassen – „Anlassen frei“.`,
        TAXI: `${r.cs} ist rollbereit. Wähle den Rollhalt – das System schlägt eine zulässige Route vor („Rollen“ oder „${r.cs} T“).`,
        DEPARTURE: `${r.cs} steht am Rollhalt und ist abflugbereit. Ist die Piste frei und der nächste Anflug weit genug weg? Dann „Line up“ oder gleich „Start frei“.`,
        CHECKIN_FINAL: `${r.cs} meldet sich im Endanflug. Erteile die Landefreigabe, sobald die Piste beim Aufsetzen frei sein wird.`,
        VACATED: `${r.cs} hat die Piste verlassen und braucht eine Rollfreigabe zu seiner Position.`,
        CHECKIN_DEP: `${r.cs} meldet sich nach dem Start. „Identified“ und eine Steigfreigabe (z. B. FL100) – Achtung auf Anflüge.`,
        CHECKIN_ARR: `${r.cs} meldet sich von Center. „Identified“, Sinkflug (z. B. 5000 ft) und ein erster Steuerkurs Richtung Gegenanflug.`,
      };
      const t = txt[r.kind];
      if (t) { this.hinted.add(key); this.hint(t, r.cs); }
    }
  }
}

function isSummer(d: Date): boolean {
  // CEST from the last Sunday in March to the last Sunday in October
  const y = d.getUTCFullYear();
  const lastSun = (m: number) => { const x = new Date(Date.UTC(y, m + 1, 0)); x.setUTCDate(x.getUTCDate() - x.getUTCDay()); return x; };
  return d >= lastSun(2) && d < lastSun(9);
}
